import React from "react";
import {
  Autocomplete,
  Box,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

const radiusOptions = [
  { label: "5 Km", value: "5-5" },
  { label: "10 Km", value: "10-10" },
  { label: "25 Km", value: "25-25" },
  { label: "50 Km", value: "50-50" },
  { label: "100 Km", value: "100-100" },
  { label: "Custom Range", value: "custom" },
];

const ConditionRow = ({
  index,
  condition,
  onChange,
  onRemove,
  availableConditions = [],
  qualificationOptions = [],
  employmentStatusOptions = [],
  jobExpertiseOptions = [],
  disabledConditionTypes = [],
  jobExpertiseLoading = false,
}) => {
  const handleChange = (field, value) => {
    if (field === "type") {
      onChange(index, {
        id: condition.id,
        type: value,
        radiusPreset: "custom",
      });
      return;
    }
    onChange(index, { ...condition, [field]: value });
  };

  const isPincodeRange = condition.type === "Pincode with Radius Range";
  const isJobLocationRange =
    condition.type === "Around Job Location with Radius Range";
  const showRadiusRange = isPincodeRange || isJobLocationRange;

  return (
    <Box className="condition-row">
      <Box
        sx={{
          display: "flex",
          gap: "12px",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
        }}
      >
        <FormControl fullWidth size="small">
          <InputLabel>Condition Type</InputLabel>
          <Select
            value={condition.type || ""}
            onChange={(event) => handleChange("type", event.target.value)}
            label="Condition Type"
          >
            <MenuItem value="" disabled>
              Select Condition
            </MenuItem>
            {availableConditions.map((option) => (
                <MenuItem
                  key={option}
                  value={option}
                  disabled={
                    disabledConditionTypes.includes(option) &&
                    option !== condition.type
                  }
                >
                {option}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <IconButton
          color="error"
          onClick={() => onRemove(index)}
          aria-label="remove condition"
        >
          <DeleteOutlineIcon fontSize="small" />
        </IconButton>
      </Box>

      {condition.type === "Age Range" && (
        <Box
          sx={{
            display: "flex",
            gap: "12px",
            alignItems: "center",
            flexWrap: "wrap",
            marginTop: "12px",
          }}
        >
          <TextField
            label="Min Age"
            type="number"
            size="small"
            value={condition.minAge || ""}
            onChange={(event) => handleChange("minAge", event.target.value)}
            inputProps={{ min: 18, max: 60 }}
          />
          <Typography>to</Typography>
          <TextField
            label="Max Age"
            type="number"
            size="small"
            value={condition.maxAge || ""}
            onChange={(event) => handleChange("maxAge", event.target.value)}
            inputProps={{ min: 18, max: 60 }}
          />
          <Typography>years</Typography>
        </Box>
      )}

      {condition.type === "Work Experience" && (
        <Box
          sx={{
            display: "flex",
            gap: "12px",
            alignItems: "center",
            flexWrap: "wrap",
            marginTop: "12px",
          }}
        >
          <TextField
            label="Min Years"
            type="number"
            size="small"
            value={condition.minYears || ""}
            onChange={(event) => handleChange("minYears", event.target.value)}
            inputProps={{ min: 0 }}
          />
          <Typography>to</Typography>
          <TextField
            label="Max Years"
            type="number"
            size="small"
            value={condition.maxYears || ""}
            onChange={(event) => handleChange("maxYears", event.target.value)}
            inputProps={{ min: 0 }}
          />
          <Typography>years</Typography>
        </Box>
      )}

      {condition.type === "Qualification" && (
        <Box sx={{ marginTop: "12px" }}>
          <Autocomplete
            multiple
            options={qualificationOptions}
            getOptionLabel={(option) => option.label || ""}
            value={condition.qualificationTypes || []}
            onChange={(event, newValue) =>
              handleChange("qualificationTypes", newValue)
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select Qualifications"
                size="small"
              />
            )}
          />
        </Box>
      )}

      {condition.type === "Employment Status" && (
        <Box sx={{ marginTop: "12px" }}>
          <Autocomplete
            multiple
            options={employmentStatusOptions}
            getOptionLabel={(option) => option.label || ""}
            value={condition.statusOptions || []}
            onChange={(event, newValue) =>
              handleChange("statusOptions", newValue)
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select Employment Status"
                size="small"
              />
            )}
          />
        </Box>
      )}

      {condition.type === "Job Expertise" && (
        <Box sx={{ marginTop: "12px" }}>
          <Autocomplete
            multiple
            options={jobExpertiseOptions}
            loading={jobExpertiseLoading}
            getOptionLabel={(option) => option.name || ""}
            value={condition.expertiseCodes || []}
            onChange={(event, newValue) =>
              handleChange("expertiseCodes", newValue)
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select Job Expertise"
                size="small"
                placeholder="Search expertise"
              />
            )}
            isOptionEqualToValue={(option, value) => option.code === value.code}
          />
        </Box>
      )}

      {condition.type === "Gender" && (
        <Box sx={{ marginTop: "12px" }}>
          <FormControl fullWidth size="small">
            <InputLabel>Select Gender</InputLabel>
            <Select
              value={condition.genderValue || ""}
              onChange={(event) => handleChange("genderValue", event.target.value)}
              label="Select Gender"
            >
              <MenuItem value={1}>Female</MenuItem>
              <MenuItem value={2}>Male</MenuItem>
              <MenuItem value={4}>Other</MenuItem>
            </Select>
          </FormControl>
        </Box>
      )}

      {isPincodeRange && (
        <Box sx={{ marginTop: "12px" }}>
          <TextField
            label="Pincode"
            size="small"
            value={condition.pincode || ""}
            onChange={(event) => {
              const value = event.target.value
                .replace(/[^0-9]/g, "")
                .slice(0, 6);
              handleChange("pincode", value);
            }}
            inputProps={{ inputMode: "numeric" }}
            placeholder="Enter 6-digit pincode"
            fullWidth
          />
        </Box>
      )}

      {isJobLocationRange && (
        <Box sx={{ marginTop: "12px" }}>
          <TextField
            label="Job ID"
            size="small"
            value={condition.jobId || ""}
            onChange={(event) => {
              const value = event.target.value
                .toUpperCase()
                .replace(/\s+/g, "");
              handleChange("jobId", value);
            }}
            placeholder="Ex: JYJ-00000"
            fullWidth
          />
        </Box>
      )}

      {showRadiusRange && (
        <Box sx={{ marginTop: "12px" }}>
          <FormControl fullWidth size="small">
            <InputLabel>Radius Range (Km)</InputLabel>
            <Select
              value={condition.radiusPreset || "custom"}
              onChange={(event) => {
                const value = event.target.value;
                handleChange("radiusPreset", value);
                if (value !== "custom") {
                  const [min, max] = value.split("-").map(Number);
                  handleChange("minRadius", min);
                  handleChange("maxRadius", max);
                }
              }}
              label="Radius Range (Km)"
            >
              {radiusOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {condition.radiusPreset === "custom" && (
            <Box
              sx={{
                display: "flex",
                gap: "12px",
                alignItems: "center",
                flexWrap: "wrap",
                marginTop: "12px",
              }}
            >
              <TextField
                label="Min Radius"
                type="number"
                size="small"
                value={condition.minRadius || ""}
                onChange={(event) =>
                  handleChange("minRadius", event.target.value)
                }
                inputProps={{ min: 0 }}
              />
              <Typography>to</Typography>
              <TextField
                label="Max Radius"
                type="number"
                size="small"
                value={condition.maxRadius || ""}
                onChange={(event) =>
                  handleChange("maxRadius", event.target.value)
                }
                inputProps={{ min: 0 }}
              />
              <Typography>Km</Typography>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default ConditionRow;
